import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import {
  Bleed,
  Box,
  Button,
  Dialog,
  Divider,
  IconCheckCircle,
  IconInfoInverted,
  IconSend,
  List,
  Skeleton,
  Stack,
  Text,
  TextLink,
  TextLinkButton,
  TooltipRenderer,
  useDialogControllers,
  useResponsiveValue,
  useToast
} from '@machobear/design-system'
import { type TurnstileInstance } from '@marsidev/react-turnstile'
import { Formik, Form as FormikForm } from 'formik'
import { omit } from 'lodash'
import noop from 'lodash/noop'
import posthog from 'posthog-js'
import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { useDispatch } from 'react-redux'

import config from '@/config'
import { startPublicChat, tenantStartChat } from '@/ports'
import Yup from '@/lib/yup'
import useCooldown from '@/hooks/use-cooldown'
import useRole from '@/hooks/use-role'
import useSessionComplete from '@/hooks/use-session-complete'
import DSLoginModal from '@/features/Auth/login-modal/ds-login-modal'
import { formatFullName } from '@/features/Auth/utils'
import { contactSimilarListingFunc } from '@/features/Listings/contact-form/inquiryMessageUtil'
import { useInquiryMessage } from '@/features/Listings/contact-form/useInquiryMessage'
import { useAddRecentMessage } from '@/features/Listings/hooks/recent-messages'
import { useAlerts } from '@/components/alert/useAlerts'
import { TextAreaField } from '@/components/ds-form/textarea-field'
import { SuccessModal } from '@/components/ds-success-modal'
import { Error } from '@/components/form'

import { Captcha } from '../captcha/captcha'
import { FieldCheckbox } from '../ds-form/check-box-field'
import DateDropdownField from '../ds-form/date-dropdown-field'
import { FieldInput } from '../ds-form/text-field'
import * as styles from './styles.css'
import type { Listing } from './types'
import { formatPriceFromListing, getIsRented, renderAddress } from './utils'

const StyledForm = styled(FormikForm)`
  width: 100%;
  label {
    margin-bottom: 0;
  }
`

type ModalProps = {
  listing: Listing
  visible: boolean
  source: 'listing-card-actions' | 'listing-detail'
  onClose: () => void
  onComplete?: () => void
}

const DSMessageModal = ({
  visible,
  listing,
  source,
  onClose,
  onComplete = noop
}: ModalProps) => {
  const { t } = useTranslation()
  const isMobile = useResponsiveValue()({
    desktop: false,
    tablet: false,
    mobile: true
  })
  const isLoggedIn = useSessionComplete()
  const {
    sendInquiryMessageAfterLogin,
    showInquiryMessageToast,
    sendMessageModalTrackingOnSuccess
  } = useInquiryMessage(isLoggedIn)
  const { sendAlert } = useAlerts()
  const dispatch = useDispatch()

  const {
    visible: innerModalVisible,
    showModal: innerShowModal,
    closeModal: innerCloseModal
  } = useDialogControllers({
    login: false,
    register: false,
    registerTenant: false,
    success: false
  })

  // Memoize onClose handlers for DSLoginModal instances
  const handleCloseLoginModal = useCallback(() => {
    innerCloseModal('login')()
  }, [innerCloseModal])

  const handleCloseRegisterModal = useCallback(() => {
    innerCloseModal('register')()
  }, [innerCloseModal])

  const handleCloseRegisterTenantModal = useCallback(() => {
    innerCloseModal('registerTenant')()
  }, [innerCloseModal])

  const isRented = getIsRented(listing)

  const formattedPrice = formatPriceFromListing(
    (listing as any).listings?.[0] ?? listing
  )

  const frequency =
    listing.price_frequency ?? (listing as any).listings?.[0]?.price_frequency
  const frequencyTextMap = {
    monthly: t('Month'),
    weekly: t('Week'),
    daily: t('Day'),
    yearly: t('Year'),
    one_time: t('One-time')
  }

  const listingId = listing.listing_id ?? listing.id

  const address = renderAddress(listing)

  const city = listing.city

  const frequencyText = frequency ? frequencyTextMap[frequency] || '' : ''
  const priceString = formattedPrice
    ? ` | ${formattedPrice}${frequencyText ? ` / ${frequencyText}` : ''}`
    : ''
  const descriptionText = `${address}, ${city}${priceString}`

  const [inquiryData, setInquiryData] = useState<{
    formValues: any
    listingId: string
    contact_similar_listings: boolean
  }>()

  const content = isLoggedIn ? (
    <Text tone="tertiary" size="medium" align="center">
      {t(
        'b.listing.view.message_sent.text',
        'The message was sent to the landlord.'
      )}
    </Text>
  ) : (
    <List type="icon" icon={<IconCheckCircle tone="positive" />} space="small">
      <Text>
        {t(
          'r.complete_registration.create.receive_faster_response.text',
          'Faster responses from landlords'
        )}
      </Text>
      <Text>
        {t(
          'r.complete_registration.create.chat_instantly.text',
          'Chat instantly'
        )}
      </Text>
      <Text>
        {t(
          'r.complete_registration.create.1_click_apply.text',
          'Apply to listings with one click'
        )}
      </Text>
      <Text>
        {t(
          'r.complete_registration.create.built_in_safety_features.text',
          'Built-in safety features'
        )}
      </Text>
    </List>
  )

  const afterLoginFn = useCallback(
    async (currentInquiryData?: typeof inquiryData) => {
      await sendInquiryMessageAfterLogin(
        () => {
          if (currentInquiryData?.contact_similar_listings) {
            sendAlert(listing)
          }
          showInquiryMessageToast()
          sendMessageModalTrackingOnSuccess(
            listingId,
            currentInquiryData?.formValues,
            currentInquiryData?.contact_similar_listings ?? false
          )
        },
        {
          listingId,
          formValues: currentInquiryData?.formValues,
          contact_similar_listings:
            currentInquiryData?.contact_similar_listings ?? false,
          source
        }
      )
    },
    [
      listingId,
      listing,
      sendInquiryMessageAfterLogin,
      showInquiryMessageToast,
      sendMessageModalTrackingOnSuccess,
      sendAlert,
      source
    ]
  )

  return (
    <>
      <Dialog
        open={visible}
        onClose={() => {
          if (!isLoggedIn) {
            posthog.capture('listing_inquiry_modal_close')
          }
          onClose()
        }}
        id={`message-modal-for-listing-${listingId}`}
        width="small"
        title={t('b.listing.view.message_modal.title', 'Check Availability')}
        description={<Text tone="secondary">{descriptionText}</Text>}
      >
        <Bleed horizontal={{ mobile: 'gutter', tablet: 'large' }}>
          <Divider />
        </Bleed>
        <Box display="flex" flexDirection={{ mobile: 'column', tablet: 'row' }}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flexStart"
            marginTop={isMobile ? 'small' : 'large'}
            className={styles.rightSectionMessageModal}
          >
            <ContactFormBody
              source={source}
              listingId={listingId}
              listing={listing}
              isRented={isRented}
              onSuccess={async ({ formValues, contact_similar_listings }) => {
                onClose()
                onComplete?.() // to notify grandparent modal like multi-list building
                if (isLoggedIn) {
                  if (contact_similar_listings) {
                    await sendAlert(listing)
                  }
                  showInquiryMessageToast()
                } else {
                  showInquiryMessageToast({
                    listing,
                    formValues,
                    contact_similar_listings
                  })

                  posthog.capture('listing_inquiry_modal_submit_success')
                }
                sendMessageModalTrackingOnSuccess(
                  listingId,
                  formValues,
                  contact_similar_listings
                )
              }}
              onError={({ code, data }) => {
                onClose()
                // onComplete() // closes building listing modal in the background
                // calling this removes login modal immediately because
                // login modal lives here (ds-message-modal) and closing its parent
                // takes it out of dom along with it.

                // the proper way to destroy login modal is to lift it high up, and
                // wrap it in a context provider with callback to close it.
                if (code === 'err.public_chat_login_required') {
                  setInquiryData(data)
                  innerShowModal('login')()
                }
              }}
              isLoggedIn={isLoggedIn}
              isMobile={isMobile ?? false}
              visible={innerModalVisible}
            />
            <Text tone="tertiary" size="xsmall">
              <Trans i18nKey="b.listing.view.message_modal.terms" t={t}>
                By proceeding, I confirm that I have read and agree to
                liv.rent&#39;s{' '}
                <TextLink
                  href="https://liv.rent/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms & Conditions
                </TextLink>{' '}
                and{' '}
                <TextLink
                  href="https://liv.rent/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </TextLink>
                .
              </Trans>
            </Text>
          </Box>
        </Box>
      </Dialog>
      <DSLoginModal
        t={t}
        mode="register"
        visible={innerModalVisible.register}
        onClose={handleCloseRegisterModal}
        eventId="web-signup-submit-inquire-sendmessage"
        listingId={parseInt(listingId)}
      />
      <DSLoginModal
        t={t}
        mode="registerTenant"
        visible={innerModalVisible.registerTenant}
        onClose={handleCloseRegisterTenantModal}
        eventId="web-signup-submit-inquire-sendmessage"
        listingId={parseInt(listingId)}
      />
      <DSLoginModal
        t={t}
        mode="login"
        visible={innerModalVisible.login}
        onClose={handleCloseLoginModal}
        eventId="web-login-submit-inquire-sendmessage"
        listingId={parseInt(listingId)}
        data={
          inquiryData
            ? {
                username: inquiryData.formValues?.email
              }
            : undefined
        }
        afterLogin={() => afterLoginFn(inquiryData)}
      />
      <SuccessModal
        dialogId="message-successfully-sent"
        content={content}
        open={innerModalVisible.success}
        onClose={innerCloseModal('success')}
        title={t('b.listing.view.message_sent.title', 'Message Sent!')}
        description={
          !isLoggedIn ? (
            <Text>
              <Trans
                i18nKey="r.complete_registration.create.check_email_to_verify.text"
                t={t}
              >
                Please check your email to verify your email address.
                <br />
                With a liv.rent account, gain access to:
              </Trans>
            </Text>
          ) : null
        }
        buttonText={
          !isLoggedIn &&
          t(
            'b.listing.view.complete_registration.text',
            'Complete Registration'
          )
        }
        onSubmit={
          !isLoggedIn
            ? async () => {
                await dispatch.session.loadChatPublicSession()
                innerShowModal('registerTenant')()
              }
            : null
        }
      />
    </>
  )
}

type ContactFormBodyProps = {
  source: 'listing-card-actions' | 'listing-detail'
  listingId: string
  listing: Listing
  isRented: boolean
  isLoggedIn: boolean
  isMobile: boolean
  visible: {
    register: boolean
    registerTenant: boolean
    success: boolean
  }
  onSuccess: (parms: {
    formValues: any
    contact_similar_listings: boolean
  }) => void
  onError: (params: {
    code: string
    data: {
      formValues: any
      listingId: string
      contact_similar_listings: boolean
    }
  }) => void
  clickId?: string
}

const ContactFormBody = ({
  listingId,
  listing,
  onSuccess,
  onError,
  isRented,
  isLoggedIn
}: ContactFormBodyProps) => {
  const { t } = useTranslation()
  const role = useRole()
  const isTenant = role === 'tenant'
  const captchaRef = useRef<TurnstileInstance | null>(null)
  const showToast = useToast()
  const [initialValues, setInitialValues] = useState<{
    full_name?: string
    email?: string
    message?: string
    move_in_date?: string
    contact_similar_listings?: boolean
  }>()

  // ====== POSTHOG EXPERIMENT - T19532 ===============================
  // Variants: ["control", "variant_hidden_input", "variant_toggle_input"]
  const variant = useFeatureFlagVariantKey('inquiry-modal-message-input')
  const isControlVariant = variant === 'control'
  // const isHiddenInputVariant = variant === 'variant_hidden_input'
  const isEditButtonVariant = variant === 'variant_toggle_input'

  const [showMessageInput, setShowMessageInput] = useState<boolean>(false)

  const showEditButton = isEditButtonVariant && !showMessageInput
  const showMessageInputField =
    isControlVariant || (isEditButtonVariant && showMessageInput)

  // ==================================================================

  // formik's isSubmitting is toggled off at the end of submission and makes submit button tempoarily enabled before closing
  // customSubmitting is required to take over that behaviour and keep button disabled until modal is closed
  const [customSubmitting, setCustomSubmitting] = useState(false)

  const { addRecentMessage } = useAddRecentMessage()

  const isMobile = useResponsiveValue()({
    desktop: false,
    tablet: false,
    mobile: true
  })
  const {
    isCityPage,
    saveFormValues,
    getFormValues,
    sendMessageModalTrackingOnSubmit
  } = useInquiryMessage(isLoggedIn)

  useEffect(() => {
    const run = async () => {
      const formValues = await getFormValues()
      setInitialValues(
        isLoggedIn
          ? formValues
          : omit(
              {
                ...formValues,
                full_name:
                  `${formValues?.first_name} ${formValues?.last_name}`.trim()
              },
              ['first_name', 'last_name']
            )
      )
    }
    run()
  }, [])

  const { isCooling, startCooldown, timeLeft } = useCooldown()

  const trackingClickId = useMemo(() => {
    const loginStr = isLoggedIn ? 'logged-' : 'listing-'
    const isCityPageStr = isCityPage ? 'city-page-' : ''
    const blockAppStr = listing.allow_applications ? '' : 'blockapp-'
    return `${loginStr}${isCityPageStr}inquire-${blockAppStr}submit-${listingId}`
  }, [isCityPage, isLoggedIn, listing.allow_applications, listingId])

  const onSubmit = async (formValues: any) => {
    setCustomSubmitting(true)

    if (!isLoggedIn) {
      const [first_name, last_name] = formatFullName(
        formValues?.full_name || ''
      )
      // full_name is FE only, BE always expect first/last_name
      formValues = omit({ ...formValues, first_name, last_name }, 'full_name')
    }

    formValues.message = formValues.message.trim()
    await saveFormValues(formValues)

    const contact_similar_listings = formValues.contact_similar_listings
    formValues = omit(formValues, 'contact_similar_listings')

    sendMessageModalTrackingOnSubmit(
      listingId,
      formValues,
      contact_similar_listings
    )

    const { response, body }: any = isLoggedIn
      ? await tenantStartChat(formValues, { listingId })
      : await startPublicChat(
          { body: omit(formValues, 'captcha') },
          { listingId },
          undefined,
          {
            headers: {
              'X-Captcha-Token': formValues.captcha
            }
          }
        )

    let XTempAccessKey = ''

    if (!isLoggedIn) {
      XTempAccessKey = response.headers.get('X-Temp-Access-Key')
      sessionStorage.setItem('X-Temp-Access-Key', XTempAccessKey)
    }

    if (!response.ok) {
      // if user email is already in our system
      if (body.message_key === 'err.public_chat_login_required') {
        onError({
          code: 'err.public_chat_login_required',
          data: {
            formValues,
            listingId,
            contact_similar_listings
          }
        })
        return
      }

      if (body.message_key === 'msg.general_error_request') {
        setCustomSubmitting(false)
        startCooldown(20)
        showToast({
          tone: 'info',
          message:
            body?.message ??
            t(
              'common.rate_limit_error',
              "It looks like you're sending messages a bit too quickly. Please pause for a short moment and try again."
            )
        })
        return
      }

      showToast({
        tone: 'critical',
        message: body?.message ?? 'Unable to contact the listing(s)'
      })
      setCustomSubmitting(false)
      return
    }

    addRecentMessage(listingId)

    if (!contact_similar_listings) {
      onSuccess({ formValues, contact_similar_listings })
      return
    }

    const contactResult = await contactSimilarListingFunc({
      isLoggedIn,
      listingId,
      formValues,
      XTempAccessKey
    })

    if (!contactResult.ok) {
      showToast({
        tone: 'critical',
        message:
          contactResult.message ?? 'Unable to contact the similar listings'
      })
    } else {
      if ((contactResult.successful_listings ?? []).length > 0) {
        contactResult.successful_listings?.forEach(listingId => {
          addRecentMessage(listingId)
        })
      }
      onSuccess({ formValues, contact_similar_listings })
    }
  }

  const validationSchema = Yup.object().shape({
    message: Yup.string().required().max(500),
    move_in_date: Yup.string().required(),
    ...(!isLoggedIn
      ? {
          full_name: Yup.string()
            .matches(
              /^[ A-Za-zÀ-Ÿ-]*$/,
              t(
                'b.profile.name.special_characters.error',
                'Cannot include special characters'
              )
            )
            .matches(
              /^[\w-]+(?:\s[\w-]+)+\s*$/,
              t('b.auth.edit.full_name.error', 'Please enter your full name')
            )
            .required(),
          email: Yup.string().email().required(),
          captcha: Yup.string().test(
            'captcha-required',
            "Captcha is required. Please wait while it's validating.",
            function (value) {
              return !shouldValidateCaptcha || !!value
            }
          )
        }
      : {})
  })

  const isDisabled = (isLoggedIn && !isTenant) || isRented
  const shouldValidateCaptcha = config.ENABLE_CONTACT_FORM_CAPTCHA && !isRented

  if (!initialValues) {
    return (
      <>
        <form noValidate style={{ width: '100%' }}>
          <Box display="flex" flexDirection="column" gap="small">
            <Skeleton.Shape height={60} />
            <Skeleton.Shape height={120} />
            <Button type="submit" disabled={true}>
              {t('b.appointment.modal.send_message.button', 'Send Message')}
            </Button>
          </Box>
        </form>
      </>
    )
  }

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({
        values,
        dirty,
        isValid,
        setFieldValue,
        setFieldError,
        setFieldTouched
      }) => {
        const contactSimilarListingsCheckbox = (
          <FieldCheckbox
            name="contact_similar_listings"
            label={
              isMobile
                ? t('Contact similar listings')
                : t('Contact similar listings on my behalf')
            }
            checked={values.contact_similar_listings ?? true}
            badge={
              <TooltipRenderer
                id="contact_similar_listings_tooltip"
                tooltip={
                  <Stack space="small">
                    <Text>{t('If checked, liv.rent will')}:</Text>
                    <List>
                      <Text>
                        {t(
                          'Automatically message up to 3 similar listings on your behalf'
                        )}
                      </Text>
                      <Text>
                        {t(
                          'Notify you of similar, newly listed properties via email'
                        )}
                      </Text>
                    </List>
                  </Stack>
                }
              >
                {({ triggerProps }) => (
                  <Box display="inline" aria-label="Tooltip" {...triggerProps}>
                    <IconInfoInverted tone="tertiary" />
                  </Box>
                )}
              </TooltipRenderer>
            }
          />
        )

        if (isLoggedIn) {
          return (
            <StyledForm noValidate>
              <Box
                paddingBottom="large"
                display="flex"
                flexDirection="column"
                gap="small"
              >
                <Box>
                  <DateDropdownField
                    name="move_in_date"
                    label={t('Move-in Date')}
                    disabled={isDisabled}
                    initialValue={initialValues.move_in_date}
                  />
                </Box>
                <Box>
                  <TextAreaField
                    name="message"
                    label={t('Message')}
                    required
                    disabled={isDisabled}
                    lines={isMobile ? 4 : 6}
                  />
                </Box>
                <Bleed top="xxsmall">{contactSimilarListingsCheckbox}</Bleed>
              </Box>

              <Bleed horizontal={{ mobile: 'gutter', tablet: 'large' }}>
                <Divider />
              </Bleed>

              <Box paddingTop="large">
                <Button
                  type="submit"
                  id={trackingClickId}
                  disabled={
                    customSubmitting ||
                    (dirty && !isValid) ||
                    isDisabled ||
                    isCooling
                  }
                >
                  {customSubmitting
                    ? t('Submitting...')
                    : isCooling
                    ? t(
                        'common.try_again_cooldown',
                        'Try again ({{cooldownSeconds}}s)',
                        { cooldownSeconds: timeLeft }
                      )
                    : t(
                        'b.appointment.modal.send_message.button',
                        'Send Message'
                      )}
                </Button>
              </Box>
            </StyledForm>
          )
        }

        return (
          <StyledForm noValidate>
            <Box
              paddingBottom="large"
              display="flex"
              flexDirection="column"
              gap="small"
            >
              <Box>
                <FieldInput
                  name="full_name"
                  disabled={isDisabled}
                  autoComplete="section-contact-form given-name"
                  label={t('b.auth.create.full_name.label', 'Full name')}
                  required
                />
              </Box>
              <Box
                display="flex"
                flexDirection={{ mobile: 'column', tablet: 'row' }}
                gap="small"
              >
                <Box width="full" flexGrow={1}>
                  <FieldInput
                    name="email"
                    disabled={isDisabled}
                    autoComplete="section-contact-form username email"
                    label={t('common.email', 'Email')}
                    required
                  />
                </Box>
                <Box width="full" flexGrow={1}>
                  <DateDropdownField
                    name="move_in_date"
                    label={t('Move-in Date')}
                    disabled={isDisabled}
                    initialValue={initialValues.move_in_date}
                  />
                </Box>
              </Box>
              {showEditButton && (
                <Box paddingY="xxsmall">
                  <Text size="small" weight="medium">
                    {t('A message will be sent to the landlord.')}{' '}
                    <TextLinkButton
                      onClick={() => {
                        posthog.capture(
                          'listing_inquiry_modal_edit_message_click'
                        )
                        setShowMessageInput(true)
                      }}
                    >
                      {t('Edit message')}
                    </TextLinkButton>
                  </Text>
                </Box>
              )}
              <Box>
                {showMessageInputField && (
                  <TextAreaField
                    name="message"
                    label={t('Message')}
                    required
                    disabled={isDisabled}
                    lines={isMobile ? 4 : 6}
                  />
                )}
              </Box>

              <Bleed top="xxsmall">{contactSimilarListingsCheckbox}</Bleed>

              {shouldValidateCaptcha && (
                <Box>
                  <Captcha
                    id="message-modal-captcha"
                    ref={captchaRef}
                    onSuccess={captcha => setFieldValue('captcha', captcha)}
                    onExpire={() => setFieldValue('captcha', '')}
                    onError={() => {
                      setFieldValue('captcha', '', false)
                      setFieldTouched('captcha', true, false)
                      setFieldError(
                        'captcha',
                        t('Captcha validation failed. Please try again.')
                      )
                    }}
                    options={{ action: 'message-modal' }}
                  />
                  <Error name="captcha" />
                </Box>
              )}
            </Box>

            <Bleed horizontal={{ mobile: 'gutter', tablet: 'large' }}>
              <Divider />
            </Bleed>

            <Box paddingTop="large">
              <Button
                icon={<IconSend />}
                type="submit"
                id={trackingClickId}
                disabled={
                  customSubmitting ||
                  !dirty ||
                  (dirty && !isValid) ||
                  isDisabled ||
                  isCooling
                }
              >
                {customSubmitting
                  ? t('Submitting...')
                  : isCooling
                  ? t(
                      'common.try_again_cooldown',
                      'Try again ({{cooldownSeconds}}s)',
                      { cooldownSeconds: timeLeft }
                    )
                  : t(
                      'b.appointment.modal.send_message.button',
                      'Send Message'
                    )}
              </Button>
            </Box>
          </StyledForm>
        )
      }}
    </Formik>
  )
}

export default DSMessageModal
